import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Card, CardContent, Grid, 
  Button, Alert, CircularProgress, Divider,
  List, ListItem, ListItemText, ListItemIcon,
  LinearProgress, Chip, FormControl, InputLabel,
  Select, MenuItem, TextField, IconButton, Tooltip,
  FormGroup, FormControlLabel, Switch
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import apiService from '../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DnsIcon from '@mui/icons-material/Dns';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import PersonIcon from '@mui/icons-material/Person';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import RefreshIcon from '@mui/icons-material/Refresh';
import VersionHistoryIcon from '@mui/icons-material/HistoryToggleOff';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend
);

const ClusterOverview = () => {
  const [clusterData, setClusterData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    users: [],
    clusters: [],
    versions: []
  });
  
  // Filter state
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedCluster, setSelectedCluster] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [sortBy, setSortBy] = useState('node_id');
  const [showFilters, setShowFilters] = useState(false);
  
  // Load filter options
  const fetchFilterOptions = async () => {
    try {
      const options = await apiService.getFilterOptions();
      setFilterOptions(options);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchClusterData = async () => {
    setLoading(true);
    try {
      const filters = {
        user: selectedUser || undefined,
        version: selectedVersion || undefined
      };
      
      const data = await apiService.getClusters(filters);
      setClusterData(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching cluster data:', err);
      setError('Failed to fetch cluster data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
    fetchClusterData();
    
    // Refresh data every 30 seconds
    const interval = setInterval(() => {
      fetchClusterData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Refetch data when filters change
  useEffect(() => {
    fetchClusterData();
  }, [selectedUser, selectedVersion, sortBy]);

  // Function to determine color based on utilization percentage
  const getUtilizationColor = (utilization) => {
    if (utilization < 30) return 'success';
    if (utilization < 70) return 'warning';
    return 'error';
  };

  const renderClusterUtilizationChart = (cluster) => {
    const chartData = {
      labels: ['Utilized', 'Available'],
      datasets: [
        {
          data: [cluster.avg_gpu_utilization, 100 - cluster.avg_gpu_utilization],
          backgroundColor: [
            'rgba(255, 99, 132, 0.8)',
            'rgba(54, 162, 235, 0.8)',
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'right',
        },
        title: {
          display: true,
          text: 'Avg GPU Utilization',
        },
      },
      cutout: '70%',
    };

    return (
      <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Doughnut data={chartData} options={options} />
      </Box>
    );
  };
  
  // Calculate total users across all clusters
  const getTotalActiveUsers = () => {
    const uniqueUsers = new Set();
    
    clusterData.forEach(cluster => {
      cluster.nodes.forEach(node => {
        if (Array.isArray(node.active_users)) {
          node.active_users.forEach(user => uniqueUsers.add(user));
        }
      });
    });
    
    return uniqueUsers.size;
  };
  
  // Handle filter and sort changes
  const handleUserChange = (event) => {
    setSelectedUser(event.target.value);
  };
  
  const handleVersionChange = (event) => {
    setSelectedVersion(event.target.value);
  };
  
  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };
  
  const handleClearFilters = () => {
    setSelectedUser('');
    setSelectedCluster('');
    setSelectedVersion('');
    setSortBy('node_id');
  };
  
  const renderFilterSection = () => {
    if (!showFilters) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            startIcon={<FilterListIcon />} 
            onClick={() => setShowFilters(true)}
            size="small"
          >
            Show Filters
          </Button>
        </Box>
      );
    }
    
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Filters & Sorting</Typography>
            <Button 
              size="small" 
              onClick={() => setShowFilters(false)}
            >
              Hide
            </Button>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="user-filter-label">Filter by User</InputLabel>
                <Select
                  labelId="user-filter-label"
                  value={selectedUser}
                  label="Filter by User"
                  onChange={handleUserChange}
                >
                  <MenuItem value="">
                    <em>All Users</em>
                  </MenuItem>
                  {filterOptions.users.map((user) => (
                    <MenuItem key={user} value={user}>{user}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="version-filter-label">Filter by Version</InputLabel>
                <Select
                  labelId="version-filter-label"
                  value={selectedVersion}
                  label="Filter by Version"
                  onChange={handleVersionChange}
                >
                  <MenuItem value="">
                    <em>All Versions</em>
                  </MenuItem>
                  {filterOptions.versions.map((version) => (
                    <MenuItem key={version} value={version}>{version}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="sort-by-label">Sort Nodes By</InputLabel>
                <Select
                  labelId="sort-by-label"
                  value={sortBy}
                  label="Sort Nodes By"
                  onChange={handleSortChange}
                >
                  <MenuItem value="node_id">Node ID</MenuItem>
                  <MenuItem value="last_activity">Least Recently Used</MenuItem>
                  <MenuItem value="active_users">Number of Active Users</MenuItem>
                  <MenuItem value="version">Version</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                sx={{ mr: 1 }}
              >
                Clear Filters
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={fetchClusterData}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  if (loading && clusterData.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && clusterData.length === 0) {
    return <Alert severity="error">{error}</Alert>;
  }

  // Get total GPU count across all clusters
  const totalGpus = clusterData.reduce((sum, cluster) => sum + cluster.total_gpus, 0);
  const totalActiveUsers = getTotalActiveUsers();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            component={RouterLink} 
            to="/" 
            startIcon={<ArrowBackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            Cluster Overview
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {loading ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : (
            <Button 
              variant="outlined" 
              size="small" 
              onClick={fetchClusterData}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
          )}
          <Typography variant="body2" color="text.secondary">
            Last updated: {lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : 'Never'}
          </Typography>
        </Box>
      </Box>
      
      {renderFilterSection()}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Summary</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <DnsIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Total Clusters" 
                    secondary={clusterData.length} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <StorageIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Total GPUs" 
                    secondary={totalGpus} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <MemoryIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Overall GPU Utilization" 
                    secondary={
                      clusterData.length > 0 ? 
                      `${clusterData.reduce((sum, cluster) => sum + cluster.avg_gpu_utilization * cluster.total_gpus, 0) / totalGpus || 0}%` : 
                      '0%'
                    } 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Total Active Users" 
                    secondary={totalActiveUsers} 
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Filters Applied</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedUser && (
                  <Chip 
                    label={`User: ${selectedUser}`} 
                    onDelete={() => setSelectedUser('')} 
                    color="primary" 
                    variant="outlined"
                  />
                )}
                {selectedVersion && (
                  <Chip 
                    label={`Version: ${selectedVersion}`} 
                    onDelete={() => setSelectedVersion('')} 
                    color="primary" 
                    variant="outlined"
                  />
                )}
                {sortBy !== 'node_id' && (
                  <Chip 
                    label={`Sorted by: ${sortBy === 'last_activity' ? 'Least Recently Used' : 
                      sortBy === 'active_users' ? 'Active Users' : 
                      sortBy === 'version' ? 'Version' : sortBy}`} 
                    onDelete={() => setSortBy('node_id')} 
                    color="secondary" 
                    variant="outlined"
                  />
                )}
                {!selectedUser && !selectedVersion && sortBy === 'node_id' && (
                  <Typography variant="body2" color="text.secondary">
                    No filters applied
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Display clusters */}
      {clusterData.map((cluster) => (
        <Card key={cluster.name} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" component="h2">
                {cluster.name}
              </Typography>
              <Box>
                <Chip 
                  icon={<StorageIcon />} 
                  label={`${cluster.total_gpus} GPUs`} 
                  size="small" 
                  sx={{ mr: 1 }} 
                />
                <Chip 
                  icon={<PersonIcon />} 
                  label={`${cluster.nodes.reduce((sum, node) => sum + (Array.isArray(node.active_users) ? node.active_users.length : 0), 0)} Users`} 
                  size="small" 
                />
              </Box>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                {renderClusterUtilizationChart(cluster)}
              </Grid>
              
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>Nodes</Typography>
                <Divider sx={{ mb: 2 }} />
                
                {/* Sort nodes based on the selected sorting criteria */}
                {[...cluster.nodes]
                  .sort((a, b) => {
                    if (sortBy === 'node_id') return a.node_id.localeCompare(b.node_id);
                    if (sortBy === 'last_activity') return a.last_activity_time?.localeCompare(b.last_activity_time || '') || 0;
                    if (sortBy === 'active_users') return (b.active_users?.length || 0) - (a.active_users?.length || 0);
                    if (sortBy === 'version') return (a.monitor_version || '').localeCompare(b.monitor_version || '');
                    return 0;
                  })
                  .map((node) => (
                  <Box key={node.node_id} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button 
                          component={RouterLink} 
                          to={`/node/${node.node_id}`} 
                          size="small" 
                          variant="text"
                          sx={{ mr: 1 }}
                        >
                          {node.node_id}
                        </Button>
                        
                        <Chip 
                          label={`${node.gpu_count} GPUs`} 
                          size="small" 
                          variant="outlined" 
                          sx={{ mr: 1 }} 
                        />
                        
                        {node.monitor_version && (
                          <Tooltip title="Monitor Version">
                            <Chip 
                              icon={<VersionHistoryIcon />} 
                              label={node.monitor_version} 
                              size="small" 
                              variant="outlined" 
                              sx={{ mr: 1 }} 
                            />
                          </Tooltip>
                        )}
                        
                        {sortBy === 'last_activity' && node.last_activity_time && (
                          <Tooltip title="Last Active">
                            <Typography variant="caption" color="text.secondary">
                              Last active: {new Date(node.last_activity_time).toLocaleString()}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                      
                      <Box>
                        {Array.isArray(node.active_users) && node.active_users.length > 0 && (
                          <Tooltip title={`Active Users: ${node.active_users.join(', ')}`}>
                            <Chip 
                              icon={<PersonIcon />} 
                              label={node.active_users.length} 
                              size="small" 
                              color="primary" 
                              variant="outlined" 
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    
                    <Tooltip title={`GPU Utilization: ${node.avg_gpu_utilization.toFixed(1)}%`}>
                      <LinearProgress 
                        variant="determinate" 
                        value={node.avg_gpu_utilization} 
                        color={getUtilizationColor(node.avg_gpu_utilization)}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Tooltip>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
      
      {clusterData.length === 0 && !loading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No clusters found with the current filter settings. Try adjusting your filters.
        </Alert>
      )}
    </Box>
  );
};

export default ClusterOverview; 